import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Download, Upload, Trash2, Filter, TrendingUp, Edit2, X, Save, History, Undo2 } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#32B4AC';
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const METRICS = [
  { key: 'roi', label: 'ROI', formatter: (v) => `${(+v).toFixed(2)}x` },
  { key: 'sales', label: 'Sales', formatter: (v) => `₹${(+v).toLocaleString('en-IN')}` },
  { key: 'spend', label: 'Spend', formatter: (v) => `₹${(+v).toLocaleString('en-IN')}` },
  { key: 'delivered', label: 'Delivered', formatter: (v) => (+v).toLocaleString('en-IN') },
  { key: 'seen', label: 'Seen', formatter: (v) => (+v).toLocaleString('en-IN') },
  { key: 'clicks', label: 'Clicks', formatter: (v) => (+v).toLocaleString('en-IN') },
];

// Distinct line colors (teal-first, then complementary)
const LINE_COLORS = [
  '#32B4AC', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4',
  '#10B981', '#F97316', '#EC4899', '#6366F1', '#14B8A6',
];

const AutomationAnalytics = ({ showToast }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Filter state
  const [selectedMetric, setSelectedMetric] = useState('roi');
  const [selectedBrands, setSelectedBrands] = useState([]); // [] => show all
  // Month+Year composite range filter — keys are "YYYY-M" strings.
  // Defaults get set from data on first load so the initial view shows everything.
  const [monthYearFrom, setMonthYearFrom] = useState('');
  const [monthYearTo, setMonthYearTo] = useState('');
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Audit-log modal state
  const [auditRow, setAuditRow] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRevertingId, setAuditRevertingId] = useState(null);

  const reloadAudit = async (rowId) => {
    setAuditLoading(true);
    try {
      const res = await fetch(
        `${API}/automation-campaigns/${rowId}/audit`,
        { headers: { 'X-User-Id': user?.id || '' } }
      );
      const data = await res.json();
      setAuditEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast?.('Failed to load history', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAuditRevert = async (entry) => {
    if (entry.reverted || entry.revert) {
      showToast?.(entry.revert ? 'Cannot revert a revert entry' : 'Already reverted', 'error');
      return;
    }
    const fieldList = (entry.changes || []).map((c) => c.field).join(', ');
    const ok = window.confirm(
      `Roll back this edit?\n\nFields: ${fieldList}\n\n` +
      `Values will be restored to what they were before this edit. ` +
      `The rollback will be recorded in the audit log.`
    );
    if (!ok) return;
    setAuditRevertingId(entry.id);
    try {
      const res = await fetch(`${API}/audit-log/${entry.id}/revert`, {
        method: 'POST',
        headers: { 'X-User-Id': user?.id || '' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Revert failed');
      }
      showToast?.('Row reverted to previous values', 'success');
      await loadRows();
      await reloadAudit(auditRow.id);
    } catch (e) {
      showToast?.(e.message || 'Failed to revert', 'error');
    } finally {
      setAuditRevertingId(null);
    }
  };
  // Measure the chart container ourselves so we only mount the recharts
  // LineChart once we know the real (>0) width/height. Definitive fix for the
  // recharts "width(-1) height(-1)" warning that fires when ResponsiveContainer
  // measures a not-yet-laid-out parent.
  const [chartSize, setChartSize] = useState({ w: 0, h: 0 });
  const chartRoRef = useRef(null);
  const attachChartRef = React.useCallback((node) => {
    // Disconnect any previous observer
    if (chartRoRef.current) {
      chartRoRef.current.disconnect();
      chartRoRef.current = null;
    }
    if (!node || typeof ResizeObserver === 'undefined') return;
    // Immediate synchronous measurement so we render on the very first paint
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setChartSize({ w: rect.width, h: rect.height });
    }
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        if (cr.width > 0 && cr.height > 0) {
          setChartSize((prev) => (prev.w === cr.width && prev.h === cr.height ? prev : { w: cr.width, h: cr.height }));
        }
      }
    });
    ro.observe(node);
    chartRoRef.current = ro;
  }, []);

  // Build the chronological list of {year, month, key, label} options that
  // covers every year that actually exists in the data PLUS Jan 2025 → Dec of
  // the max data year + 1 (so the user can extend forward without a code
  // change). Sorted ascending.
  const monthYearOptions = useMemo(() => {
    const years = new Set([2025, 2026, new Date().getFullYear()]);
    rows.forEach((r) => { years.add(Number(r.year) || 2025); });
    const min = Math.min(...years, 2025);
    const max = Math.max(...years);
    const opts = [];
    for (let y = min; y <= max; y += 1) {
      for (let m = 1; m <= 12; m += 1) {
        opts.push({ key: `${y}-${m}`, year: y, month: m, label: `${MONTH_NAMES[m - 1]} ${y}` });
      }
    }
    return opts;
  }, [rows]);

  // Set default filter range on first data load: whole span of rows.
  useEffect(() => {
    if (monthYearFrom && monthYearTo) return;
    if (rows.length === 0) return;
    const withKey = rows.map((r) => ({ year: Number(r.year) || 2025, month: r.month_num }));
    withKey.sort((a, b) => (a.year - b.year) || (a.month - b.month));
    setMonthYearFrom(`${withKey[0].year}-${withKey[0].month}`);
    setMonthYearTo(`${withKey[withKey.length - 1].year}-${withKey[withKey.length - 1].month}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  // Convert `"YYYY-M"` → sortable ordinal (year*12 + month).
  const keyOrdinal = (key) => {
    if (!key) return -Infinity;
    const [y, m] = key.split('-').map(Number);
    return y * 12 + m;
  };
  const rowOrdinal = (r) => (Number(r.year) || 2025) * 12 + Number(r.month_num);

  useEffect(() => {
    loadRows();
  }, []);

  const loadRows = async () => {
    try {
      const res = await fetch(`${API}/automation-campaigns`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast?.('Failed to load automation analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const allBrands = useMemo(
    () => Array.from(new Set(rows.map((r) => r.brand))).sort(),
    [rows]
  );

  // Auto-select all brands the first time data arrives
  useEffect(() => {
    if (selectedBrands.length === 0 && allBrands.length > 0) {
      setSelectedBrands(allBrands);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBrands.length]);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/automation-campaigns/template`);
      const data = await res.json();
      const blob = new Blob([data.template], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'automation_campaigns_template.csv';
      a.click();
      URL.revokeObjectURL(url);
      showToast?.('Template downloaded', 'success');
    } catch (e) {
      showToast?.('Failed to download template', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.csv$/i)) {
      showToast?.('Please upload a .csv file', 'error');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      let text = await file.text();
      // Strip UTF-8 BOM if present (Excel exports often include it)
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
      const res = await fetch(`${API}/automation-campaigns/upload-csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user?.id || '',
        },
        body: JSON.stringify({ csv_content: text }),
      });
      const result = await res.json();
      if (!res.ok) {
        const detail = typeof result.detail === 'string'
          ? result.detail
          : 'Upload failed';
        throw new Error(detail);
      }
      const total = (result.added || 0) + (result.updated || 0);
      if (total === 0 && result.errors?.length) {
        showToast?.(`No rows imported. ${result.errors[0]}`, 'error');
      } else {
        const parts = [];
        if (result.added) parts.push(`Added ${result.added}`);
        if (result.updated) parts.push(`Updated ${result.updated}`);
        if (result.skipped_empty) parts.push(`Skipped ${result.skipped_empty} empty`);
        if (result.errors?.length) parts.push(`${result.errors.length} error${result.errors.length === 1 ? '' : 's'}`);
        showToast?.(parts.join(' · '), result.errors?.length ? 'error' : 'success');
      }
      await loadRows();
    } catch (err) {
      showToast?.(err.message || 'Failed to upload CSV', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const toggleBrand = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const filteredRows = useMemo(() => {
    const fromOrd = keyOrdinal(monthYearFrom);
    const toOrd = keyOrdinal(monthYearTo);
    return rows.filter((r) => {
      const ord = rowOrdinal(r);
      const inRange = (monthYearFrom ? ord >= fromOrd : true) && (monthYearTo ? ord <= toOrd : true);
      return inRange && (selectedBrands.length === 0 || selectedBrands.includes(r.brand));
    });
  }, [rows, monthYearFrom, monthYearTo, selectedBrands]);

  // Pivot to recharts shape, bucketed by (year, month). Chronological ascending.
  const chartData = useMemo(() => {
    const visibleBrands = selectedBrands.length > 0 ? selectedBrands : allBrands;
    const buckets = {};
    filteredRows.forEach((r) => {
      const year = Number(r.year) || 2025;
      const key = `${year}-${String(r.month_num).padStart(2, '0')}`;
      if (!buckets[key]) {
        buckets[key] = {
          key,
          year,
          month_num: r.month_num,
          month: `${MONTH_NAMES[r.month_num - 1]} ${year}`,
        };
        visibleBrands.forEach((b) => { buckets[key][b] = null; });
      }
      buckets[key][r.brand] = r[selectedMetric] ?? 0;
    });
    return Object.values(buckets).sort(
      (a, b) => (a.year - b.year) || (a.month_num - b.month_num)
    );
  }, [filteredRows, selectedMetric, selectedBrands, allBrands]);

  const visibleBrands =
    selectedBrands.length > 0 ? selectedBrands : allBrands;

  const metricMeta = METRICS.find((m) => m.key === selectedMetric) || METRICS[0];

  const yFormatter = (val) => {
    if (val == null) return '';
    if (selectedMetric === 'roi') return `${val}x`;
    if (val >= 1_00_000) return `${(val / 1_00_000).toFixed(1)}L`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    return val;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div
        className="bg-white rounded-xl shadow-lg p-3 border border-slate-200"
        data-testid="automation-chart-tooltip"
      >
        <p className="text-sm font-semibold text-slate-900 mb-1">{label}</p>
        {payload
          .filter((p) => p.value != null)
          .map((p) => (
            <div key={p.dataKey} className="flex items-center gap-2 text-xs py-0.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: p.color }}
              />
              <span className="text-slate-600">{p.dataKey}</span>
              <span className="ml-auto font-semibold text-slate-900">
                {metricMeta.formatter(p.value)}
              </span>
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="p-8 max-w-[1500px] mx-auto" data-testid="automation-analytics-page">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={24} style={{ color: TEAL }} />
            <h1 className="text-3xl font-bold text-slate-900">
              Automation Campaign Analytics
            </h1>
          </div>
          <p className="text-slate-500 text-sm">
            Monthly trends across automation flows. Independent from Campaign Requests &amp; All Campaigns Data.
          </p>
        </div>

        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={downloadTemplate}
              data-testid="automation-download-template"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-xl text-slate-700 hover:bg-slate-50 transition"
            >
              <Download size={16} />
              <span className="text-sm font-medium">Template</span>
            </button>
            <label
              data-testid="automation-upload-csv"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-white cursor-pointer transition hover:opacity-90"
              style={{ backgroundColor: TEAL }}
            >
              <Upload size={16} />
              <span className="text-sm font-medium">
                {uploading ? 'Uploading…' : 'Upload CSV'}
              </span>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>
        )}
      </div>

      {/* Filters */}
      <div
        className="bg-white rounded-xl border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4"
        data-testid="automation-filters"
      >
        {/* Campaign Name Multi-Select */}
        <div className="relative">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Campaign Names
          </label>
          <button
            onClick={() => setBrandPickerOpen((o) => !o)}
            data-testid="automation-brand-filter"
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 hover:border-slate-400 transition"
          >
            <span className="truncate">
              {selectedBrands.length === 0
                ? 'No campaigns'
                : selectedBrands.length === allBrands.length
                ? `All campaigns (${allBrands.length})`
                : `${selectedBrands.length} selected`}
            </span>
            <Filter size={14} className="flex-shrink-0 text-slate-400" />
          </button>
          {brandPickerOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setBrandPickerOpen(false)}
              />
              <div className="absolute top-full mt-1 left-0 right-0 z-20 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                <div className="p-2 border-b border-slate-100 flex gap-2">
                  <button
                    onClick={() => setSelectedBrands(allBrands)}
                    className="text-xs px-2 py-1 rounded hover:bg-slate-100 text-slate-600"
                  >
                    Select all
                  </button>
                  <button
                    onClick={() => setSelectedBrands([])}
                    className="text-xs px-2 py-1 rounded hover:bg-slate-100 text-slate-600"
                  >
                    Clear
                  </button>
                </div>
                {allBrands.length === 0 && (
                  <p className="text-xs text-slate-400 p-3 text-center">
                    No campaigns yet
                  </p>
                )}
                {allBrands.map((b) => {
                  const checked = selectedBrands.includes(b);
                  return (
                    <label
                      key={b}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleBrand(b)}
                        className="rounded"
                        style={{ accentColor: TEAL }}
                      />
                      <span className="text-slate-700 truncate">{b}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Metric Selector */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Metric
          </label>
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            data-testid="automation-metric-select"
            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': TEAL }}
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* From Month + Year */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            From month + year
          </label>
          <select
            value={monthYearFrom}
            onChange={(e) => {
              const v = e.target.value;
              setMonthYearFrom(v);
              if (keyOrdinal(v) > keyOrdinal(monthYearTo)) setMonthYearTo(v);
            }}
            data-testid="automation-month-from"
            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700"
          >
            {monthYearOptions.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* To Month + Year */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            To month + year
          </label>
          <select
            value={monthYearTo}
            onChange={(e) => {
              const v = e.target.value;
              setMonthYearTo(v);
              if (keyOrdinal(v) < keyOrdinal(monthYearFrom)) setMonthYearFrom(v);
            }}
            data-testid="automation-month-to"
            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700"
          >
            {monthYearOptions.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Chart */}
      <div
        className="bg-white rounded-xl border border-slate-200 p-6"
        data-testid="automation-chart-container"
      >
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {metricMeta.label} by month
          </h2>
          <span className="text-xs text-slate-500" data-testid="automation-chart-caption">
            {visibleBrands.length} campaign{visibleBrands.length === 1 ? '' : 's'} ·
            {' '}
            {monthYearOptions.find((o) => o.key === monthYearFrom)?.label || '—'}–{monthYearOptions.find((o) => o.key === monthYearTo)?.label || '—'}
          </span>
        </div>

        {loading ? (
          <div className="h-80 flex items-center justify-center text-slate-400 text-sm">
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="h-80 flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
            <p>No automation data yet.</p>
            {isAdmin && (
              <p className="text-xs">
                Upload a CSV with columns: Month, Campaign Name, Sales, Spend, Sent, Delivered, Seen, Clicks, ROI
              </p>
            )}
          </div>
        ) : (
          <div ref={attachChartRef} className="h-96" style={{ height: 384, width: '100%' }} data-testid="automation-line-chart">
            {chartSize.w > 0 && chartSize.h > 0 && (
            <LineChart
              width={chartSize.w}
              height={chartSize.h}
              data={chartData}
              margin={{ top: 10, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="month"
                tick={{ fill: '#64748B', fontSize: 12 }}
                axisLine={{ stroke: '#CBD5E1' }}
              />
              <YAxis
                tick={{ fill: '#64748B', fontSize: 12 }}
                tickFormatter={yFormatter}
                axisLine={{ stroke: '#CBD5E1' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '12px', fontSize: '12px' }}
                iconType="circle"
              />
              {visibleBrands.map((brand, idx) => (
                <Line
                  key={brand}
                  type="monotone"
                  dataKey={brand}
                  stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              ))}
            </LineChart>
            )}
          </div>
        )}
      </div>

      {/* Data table preview */}
      {rows.length > 0 && (
        <div
          className="bg-white rounded-xl border border-slate-200 mt-6 overflow-hidden"
          data-testid="automation-data-table"
        >
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">
              Raw data ({filteredRows.length} rows)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Month + Year</th>
                  <th className="px-4 py-2 text-left font-semibold">Campaign Name</th>
                  <th className="px-4 py-2 text-right font-semibold">Sales</th>
                  <th className="px-4 py-2 text-right font-semibold">Spend</th>
                  <th className="px-4 py-2 text-right font-semibold">Sent</th>
                  <th className="px-4 py-2 text-right font-semibold">Delivered</th>
                  <th className="px-4 py-2 text-right font-semibold">Seen</th>
                  <th className="px-4 py-2 text-right font-semibold">Clicks</th>
                  <th className="px-4 py-2 text-right font-semibold">ROI</th>
                  {isAdmin && <th className="px-4 py-2 w-12" />}
                </tr>
              </thead>
              <tbody>
                {filteredRows
                  .slice()
                  .sort((a, b) => {
                    const ao = rowOrdinal(a);
                    const bo = rowOrdinal(b);
                    if (ao !== bo) return ao - bo;
                    return a.brand.localeCompare(b.brand);
                  })
                  .map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`automation-row-${r.id}`}>
                      <td className="px-4 py-2 text-slate-700">{r.month_name} {Number(r.year) || 2025}</td>
                      <td className="px-4 py-2 text-slate-900 font-medium">{r.brand}</td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {(+r.sales).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {(+r.spend).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {(+r.sent).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {(+r.delivered).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {(+r.seen).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {(+r.clicks).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold" style={{ color: TEAL }}>
                        {(+r.roi).toFixed(2)}x
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={async () => {
                                setAuditRow(r);
                                setAuditLoading(true);
                                setAuditEntries([]);
                                try {
                                  const res = await fetch(
                                    `${API}/automation-campaigns/${r.id}/audit`,
                                    { headers: { 'X-User-Id': user?.id || '' } }
                                  );
                                  const data = await res.json();
                                  setAuditEntries(Array.isArray(data) ? data : []);
                                } catch (e) {
                                  showToast?.('Failed to load history', 'error');
                                } finally {
                                  setAuditLoading(false);
                                }
                              }}
                              data-testid={`automation-history-row-${r.id}`}
                              className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition"
                              title="View edit history"
                            >
                              <History size={14} />
                            </button>
                            <button
                              onClick={() => setEditingRow({
                                ...r,
                                year: Number(r.year) || 2025,
                                month_num: r.month_num,
                              })}
                              data-testid={`automation-edit-row-${r.id}`}
                              className="p-1.5 rounded hover:bg-teal-50 text-slate-400 hover:text-teal-700 transition"
                              title="Edit row"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm('Delete this row?')) return;
                                try {
                                  const res = await fetch(
                                    `${API}/automation-campaigns/${r.id}`,
                                    {
                                      method: 'DELETE',
                                      headers: { 'X-User-Id': user?.id || '' },
                                    }
                                  );
                                  if (!res.ok) throw new Error('Delete failed');
                                  showToast?.('Row deleted', 'success');
                                  loadRows();
                                } catch (e) {
                                  showToast?.('Failed to delete row', 'error');
                                }
                              }}
                              data-testid={`automation-delete-row-${r.id}`}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Row Modal */}
      {editingRow && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="automation-edit-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setEditingRow(null); }}
        >
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Edit2 size={16} className="text-teal-600" /> Edit automation row
              </h3>
              <button onClick={() => setEditingRow(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="automation-edit-close">
                <X size={16} />
              </button>
            </div>

            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Month + Year</label>
                <select
                  value={`${editingRow.year}-${editingRow.month_num}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split('-').map(Number);
                    setEditingRow({ ...editingRow, year: y, month_num: m, month_name: MONTH_NAMES[m - 1] });
                  }}
                  data-testid="automation-edit-month-year"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
                >
                  {monthYearOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Campaign Name</label>
                <input
                  type="text"
                  value={editingRow.brand || ''}
                  onChange={(e) => setEditingRow({ ...editingRow, brand: e.target.value })}
                  data-testid="automation-edit-brand"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
                />
              </div>
              {[
                { k: 'sales', label: 'Sales' },
                { k: 'spend', label: 'Spend' },
                { k: 'sent', label: 'Sent' },
                { k: 'delivered', label: 'Delivered' },
                { k: 'seen', label: 'Seen' },
                { k: 'clicks', label: 'Clicks' },
                { k: 'roi', label: 'ROI (x)' },
              ].map((f) => (
                <div key={f.k}>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{f.label}</label>
                  <input
                    type="number"
                    step="any"
                    value={editingRow[f.k] ?? 0}
                    onChange={(e) => setEditingRow({ ...editingRow, [f.k]: e.target.value })}
                    data-testid={`automation-edit-${f.k}`}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm tabular-nums"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-slate-100">
              <button
                onClick={() => setEditingRow(null)}
                className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                disabled={savingEdit}
                onClick={async () => {
                  setSavingEdit(true);
                  try {
                    const payload = {
                      month: editingRow.month_num,
                      year: editingRow.year,
                      brand: editingRow.brand,
                      sales: editingRow.sales,
                      spend: editingRow.spend,
                      sent: editingRow.sent,
                      delivered: editingRow.delivered,
                      seen: editingRow.seen,
                      clicks: editingRow.clicks,
                      roi: editingRow.roi,
                    };
                    const res = await fetch(`${API}/automation-campaigns/${editingRow.id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', 'X-User-Id': user?.id || '' },
                      body: JSON.stringify(payload),
                    });
                    const result = await res.json();
                    if (!res.ok) throw new Error(result.detail || 'Update failed');
                    showToast?.('Row updated', 'success');
                    setEditingRow(null);
                    await loadRows();
                  } catch (err) {
                    showToast?.(err.message || 'Failed to update row', 'error');
                  } finally {
                    setSavingEdit(false);
                  }
                }}
                data-testid="automation-edit-save"
                className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90 inline-flex items-center gap-1.5"
                style={{ backgroundColor: TEAL }}
              >
                <Save size={13} /> {savingEdit ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit-log History Modal */}
      {auditRow && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="automation-audit-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setAuditRow(null); }}
        >
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <History size={16} className="text-slate-500" /> Edit history
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {auditRow.brand} · {MONTH_NAMES[(auditRow.month_num || 1) - 1]} {auditRow.year || 2025}
                </p>
              </div>
              <button
                onClick={() => setAuditRow(null)}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                data-testid="automation-audit-close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {auditLoading ? (
                <div className="text-slate-400 text-sm text-center py-8">Loading history…</div>
              ) : auditEntries.length === 0 ? (
                <div className="text-slate-400 text-sm text-center py-8" data-testid="automation-audit-empty">
                  No edits recorded yet for this row.
                </div>
              ) : (
                <ul className="space-y-3" data-testid="automation-audit-list">
                  {auditEntries.map((entry) => {
                    const ts = entry.timestamp ? new Date(entry.timestamp) : null;
                    return (
                      <li
                        key={entry.id}
                        className="border border-slate-200 rounded-lg p-3 bg-slate-50/50"
                      >
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="text-xs text-slate-700 flex items-center flex-wrap gap-1.5">
                            <span className="font-semibold text-slate-900">
                              {entry.user_name || entry.user_email || 'Unknown user'}
                            </span>
                            {entry.user_role && (
                              <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] uppercase tracking-wider">
                                {entry.user_role}
                              </span>
                            )}
                            {entry.revert && (
                              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider font-semibold">
                                Revert
                              </span>
                            )}
                            {entry.reverted && (
                              <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] uppercase tracking-wider font-semibold">
                                Reverted
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-[11px] text-slate-500 tabular-nums">
                              {ts ? ts.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                            </div>
                            {isAdmin && !entry.revert && !entry.reverted && (
                              <button
                                onClick={() => handleAuditRevert(entry)}
                                disabled={auditRevertingId === entry.id}
                                data-testid={`automation-audit-revert-${entry.id}`}
                                className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded border border-amber-300 text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                title="Revert this edit"
                              >
                                <Undo2 size={11} />
                                {auditRevertingId === entry.id ? '…' : 'Revert'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          {(entry.changes || []).map((c, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span className="font-medium text-slate-600 min-w-[80px] capitalize">
                                {c.field.replace('_', ' ')}
                              </span>
                              <span className="text-red-500 line-through tabular-nums">{String(c.before ?? '—')}</span>
                              <span className="text-slate-400">→</span>
                              <span className="text-teal-700 font-semibold tabular-nums">{String(c.after ?? '—')}</span>
                            </div>
                          ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationAnalytics;
